import React, { useState, useEffect } from 'react'

const CookieConsentBanner = () => {
  const [showBanner, setShowBanner] = useState(false)
  const [showPreferences, setShowPreferences] = useState(false)
  const [consent, setConsent] = useState({
    ads: false,
    analytics: false,
    functionality: true,
  })

  useEffect(() => {
    // Set up consent listener handling
    window.consentListeners = []
    window.addConsentListener = (callback) => {
      window.consentListeners.push(callback)
    }

    // Call listeners when consent changes
    const notifyConsentListeners = (consentState) => {
      window.consentListeners.forEach((callback) =>
        callback({
          adConsentGranted: consentState.ads,
          analyticsConsentGranted: consentState.analytics,
          functionalityConsentGranted: consentState.functionality,
        }),
      )
    }

    // Add notifyConsentListeners to window for GTM
    window.notifyConsentListeners = notifyConsentListeners
  }, [])

  useEffect(() => {
    const storedConsent = localStorage.getItem('gatsby-gdpr-google-tagmanager')
    window.dataLayer = window.dataLayer || []

    if (!storedConsent) {
      // Set default consent states
      window.dataLayer.push({
        event: 'consent_default',
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        analytics_storage: 'denied',
        functionality_storage: 'denied',
      })
      setShowBanner(true)
    } else {
      const consentObj = JSON.parse(storedConsent)
      setConsent(consentObj)

      // Trigger onUserConsent with the stored consent
      onUserConsent(consentObj)

      // Load GTM after consent is processed
      loadGTM()
    }

    // Expose function to show preferences globally
    window.showCookiePreferences = () => {
      setShowPreferences(true)
      setShowBanner(true)
    }
  }, [])

  const loadGTM = () => {
    if (!document.getElementById('gtm-script')) {
      const script = document.createElement('script')
      script.id = 'gtm-script'
      script.src = `https://www.googletagmanager.com/gtm.js?id=GTM-TLSPK2H`
      script.async = true
      document.head.appendChild(script)
    }
  }

  const onUserConsent = (consentObj) => {
    // Map consent object to Google's consent state keys
    const consentModeStates = {
      ad_storage: consentObj.ads ? 'granted' : 'denied',
      ad_user_data: consentObj.ads ? 'granted' : 'denied',
      ad_personalization: consentObj.ads ? 'granted' : 'denied',
      analytics_storage: consentObj.analytics ? 'granted' : 'denied',
      functionality_storage: 'granted', // Always granted
    }

    // Update GTM consent state
    updateConsentState(consentModeStates)
    window.notifyConsentListeners?.(consentObj)
  }

  const updateConsentState = (consentModeStates) => {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      event: 'consent_update',
      ...consentModeStates,
    })
  }

  const handleAcceptAll = () => {
    const newConsent = {
      ads: true,
      analytics: true,
      functionality: true,
    }
    localStorage.setItem('gatsby-gdpr-google-tagmanager', JSON.stringify(newConsent))
    setConsent(newConsent)

    // Trigger onUserConsent
    onUserConsent(newConsent)

    setShowBanner(false)
  }

  const handleAcceptSelection = () => {
    const newConsent = {
      ads: consent.ads,
      analytics: consent.analytics,
      functionality: true, // Always true
    }
    localStorage.setItem('gatsby-gdpr-google-tagmanager', JSON.stringify(newConsent))
    setConsent(newConsent)

    // Trigger onUserConsent
    onUserConsent(newConsent)

    setShowBanner(false)
  }

  const handleCheckboxChange = (event) => {
    const { name, checked } = event.target
    setConsent((prevConsent) => ({
      ...prevConsent,
      [name]: checked,
    }))
  }

  return (
    showBanner && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-800 bg-opacity-75">
        {!showPreferences ? (
          <div className="bg-white rounded-lg p-6 shadow-lg max-w-lg w-full">
            <h2 className="text-xl font-bold mb-4">We use cookies!</h2>
            <p className="mb-4 text-gray-600">
              We use cookies to enhance your experience. Choose your preferences or accept all.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                className="bg-gray-200 text-gray-700 px-4 py-2 rounded"
                onClick={() => setShowPreferences(true)}
              >
                Preferences
              </button>
              <button className="bg-blue-600 text-white px-4 py-2 rounded" onClick={handleAcceptAll}>
                Accept All
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg p-6 shadow-lg max-w-lg w-full">
            <h2 className="text-xl font-bold mb-4">Cookie Preferences</h2>
            <div className="mb-4">
              <label className="flex items-center space-x-2">
                <input type="checkbox" name="ads" checked={consent.ads} onChange={handleCheckboxChange} />
                <span>Ads</span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="analytics"
                  checked={consent.analytics}
                  onChange={handleCheckboxChange}
                />
                <span>Analytics</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="checkbox" name="functionality" checked={consent.functionality} disabled />
                <span>Functionality (Always Enabled)</span>
              </label>
            </div>
            <div className="flex justify-end space-x-4">
              <button
                className="bg-gray-200 text-gray-700 px-4 py-2 rounded"
                onClick={() => setShowPreferences(false)}
              >
                Back
              </button>
              <button className="bg-blue-600 text-white px-4 py-2 rounded" onClick={handleAcceptSelection}>
                Save Preferences
              </button>
            </div>
          </div>
        )}
      </div>
    )
  )
}

export default CookieConsentBanner
